<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';

const auth = useAuthStore();
const router = useRouter();
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let disposed = false;
const isRecovering = ref(false);

async function retrySession(): Promise<void> {
  clearTimeout(retryTimer);
  isRecovering.value = true;
  await auth.initialize();
  if (disposed) return;
  if (auth.isInitialized) {
    // 再次运行角色及登录校验，恢复原来的访问地址。
    await router.replace({ path: router.currentRoute.value.fullPath, force: true });
  } else {
    retryTimer = setTimeout(retrySession, 5000);
  }
  isRecovering.value = false;
}

watch(() => auth.sessionCheckError, (error) => {
  clearTimeout(retryTimer);
  if (error) retryTimer = setTimeout(retrySession, 5000);
}, { immediate: true });

onBeforeUnmount(() => {
  disposed = true;
  clearTimeout(retryTimer);
});
</script>

<template>
  <main
    v-if="auth.sessionCheckError || isRecovering"
    class="session-recovery"
    role="status"
    aria-live="polite"
  >
    <section class="surface">
      <h1>服务暂时不可用</h1>
      <p>{{ auth.sessionCheckError }}</p>
      <button
        type="button"
        :disabled="auth.isCheckingSession"
        @click="retrySession"
      >
        {{ auth.isCheckingSession ? '正在重试…' : '立即重试' }}
      </button>
    </section>
  </main>
  <RouterView v-else />
</template>

<style scoped>
.session-recovery { display: grid; min-height: 100vh; place-items: center; padding: 24px; }
.session-recovery section { padding: 32px; max-width: 480px; }
.session-recovery h1 { margin-top: 0; font-size: 22px; }
.session-recovery p { color: var(--color-ink-500); line-height: 1.7; }
.session-recovery button { border: 0; border-radius: var(--radius-sm); padding: 10px 20px;
  background: var(--color-brand-600); color: white; cursor: pointer; }
.session-recovery button:disabled { opacity: .6; cursor: wait; }
</style>
